import React, { useId } from 'react';
import LoadingSpinner from '../LoadingSpinner';
import SearchableSelect from '../ui/SearchableSelect';
import { accessOptions, isAboveCaller, type AccessRow, type AccessValue } from './projectAccess';
import { projectRef } from './personLines';
import { ROLE_LABELS } from '../../utils/roles';
import type { AccessLoad } from './useProjectAccess';

interface Props {
  load: AccessLoad;
  rows: readonly AccessRow[];
  errors: Readonly<Record<string, string>>;
  /** The signed-in person's role; options never go above it. */
  callerRole: string | null | undefined;
  disabled: boolean;
  onChoose: (projectId: string, value: AccessValue) => void;
}

/**
 * What the login may do on each project. A project with no choice follows the login's workspace role; a choice
 * replaces it on that project only. Saved with the page.
 */
const PersonProjectAccessSection: React.FC<Props> = ({ load, rows, errors, callerRole, disabled, onChoose }) => {
  const uid = useId();
  return (
    <section className="settings-card ep-section" aria-labelledby={`${uid}-title`}>
      <h2 id={`${uid}-title`} className="ap-section-title">Access on projects</h2>
      <p className="settings-muted-xs">
        Choose a different access role for a project, or leave it on the workspace role. &quot;No access&quot; hides the
        project from this person. Changes are saved with the page.
      </p>
      {load.status === 'loading' ? (
        <LoadingSpinner compact message="Loading access…" />
      ) : rows.length === 0 ? (
        <p className="settings-muted-xs">This workspace has no projects yet.</p>
      ) : (
        <ul className="epa-list" aria-label="Access on projects">
          {rows.map(row => {
            const locked = disabled || !!row.loadError || isAboveCaller(row.workspaceRole, callerRole);
            const errorId = `${uid}-${row.projectId}-error`;
            return (
              <li key={row.projectId} className="epa-row">
                <div className="epl-project">
                  <strong className="epl-project__name">{row.projectName}</strong>
                  <span className="epl-project__ref">{projectRef(row.projectId)}</span>
                </div>
                <div className="epa-control">
                  <SearchableSelect
                    value={row.value}
                    onChange={v => onChoose(row.projectId, v as AccessValue)}
                    options={accessOptions(row.workspaceRole, callerRole)}
                    ariaLabel={`Access on ${row.projectName}`}
                    disabled={locked}
                  />
                  {row.loadError && <span className="epl-project__note" role="status">{row.loadError}</span>}
                  {!row.loadError && isAboveCaller(row.workspaceRole, callerRole) && (
                    <span className="epl-project__note">
                      {row.workspaceRole ? ROLE_LABELS[row.workspaceRole] : 'This role'} access is above yours, so only an owner can change it.
                    </span>
                  )}
                  {errors[row.projectId] && <p id={errorId} className="form-error-text" role="alert">{errors[row.projectId]}</p>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default PersonProjectAccessSection;
